import Echo from 'laravel-echo'
import Pusher from 'pusher-js'
import { AppProblemModel } from '@/services/api/AppProblemModel'
import { problemTitle } from '@/services/api/problemTitle'
import { Normalization } from '~/utils/normalization'

interface ChannelAuthorizationData {
  auth: string
  channel_data?: string
  shared_secret?: string
}

const isChannelAuthorizationData = (value: unknown): value is ChannelAuthorizationData =>
  Normalization.isRecord(value) && typeof value['auth'] === 'string'

const broadcastAuthorizationError = (error: unknown): Error => {
  const problem = AppProblemModel.normalize(error)

  return new Error(problemTitle(problem), { cause: problem })
}

export default defineNuxtPlugin(() => {
  const runtimeConfig = useRuntimeConfig()
  const sanctumClient = useApiRequest()

  let echo: Echo<'reverb'> | null = null
  const departing = new Set<Echo<'reverb'>>()

  const appKey = runtimeConfig.public.reverbAppKey
  // NUXT_PUBLIC_REVERB_URL is one fact (scheme + host + port), not three --
  // env.schema.json guarantees it parses to a websocket origin. A port
  // matching the scheme's own default (wss:443, ws:80) is normalized away by
  // URL, so an empty `.port` falls back to that scheme default, not "unset".
  const reverbUrl = new URL(runtimeConfig.public.reverbUrl)
  const reverbHost = reverbUrl.hostname
  const prefersTls = reverbUrl.protocol === 'wss:'
  const reverbPort = Number(reverbUrl.port || (prefersTls ? 443 : 80))
  const reverbActivityTimeout = runtimeConfig.public.reverbActivityTimeout
  const reverbPongTimeout = runtimeConfig.public.reverbPongTimeout
  const isConfigured = Boolean(appKey && reverbHost && reverbPort)

  const ensureConnected = () => {
    if (echo || !isConfigured) {
      return echo
    }

    Object.assign(window, { Pusher })

    echo = new Echo({
      broadcaster: 'reverb',
      key: appKey,
      wsHost: reverbHost,
      wsPort: reverbPort,
      wssPort: reverbPort,
      forceTLS: prefersTls,
      enabledTransports: ['ws', 'wss'],
      disableStats: true,
      ...(reverbUrl.pathname === '/' ? {} : { wsPath: reverbUrl.pathname }),
      activityTimeout: reverbActivityTimeout,
      pongTimeout: reverbPongTimeout,
      authorizer: (channel: { name: string }) => ({
        authorize: (
          socketId: string,
          callback: (_error: Error | null, _response: ChannelAuthorizationData | null) => void
        ) => {
          void (async () => {
            try {
              const response = await sanctumClient('/api/broadcasting/auth', {
                method: 'POST',
                body: {
                  socket_id: socketId,
                  channel_name: channel.name
                }
              })

              if (!isChannelAuthorizationData(response)) {
                callback(new Error('Invalid channel authorization response'), null)
                return
              }
              callback(null, response)
            } catch (error: unknown) {
              callback(broadcastAuthorizationError(error), null)
            }
          })().catch((error: unknown) => {
            callback(broadcastAuthorizationError(error), null)
          })
        }
      })
    })

    return echo
  }

  const getInstance = () => echo

  // A connection whose teardown failed stays queued for retry and is never reused as the live one.
  const disconnect = () => {
    if (echo) {
      departing.add(echo)
      echo = null
    }

    const failures: unknown[] = []
    for (const connection of departing) {
      try {
        connection.disconnect()
        departing.delete(connection)
      } catch (error: unknown) {
        failures.push(error)
      }
    }

    if (failures.length > 0) {
      throw failures[0]
    }
  }

  return {
    provide: {
      notificationsEcho: {
        isConfigured,
        ensureConnected,
        getInstance,
        disconnect
      }
    }
  }
})
